/**
 * How to play (docs/spec/04 §6): a page per idea, each with the real sprites acting it out and a
 * few lines under them. Left/right, or a tap on either side, turn the pages.
 */

import type { Screen } from '../../render/screen';
import { CAPSULE_LOOK, drawCapsule } from '../../render/sprites';
import { UiScene } from '../kit/base';
import { drawPanel } from '../kit/chrome';
import type { Rect } from '../kit/menuModel';
import { clampIndex } from '../kit/menuModel';
import {
  ACCENT, blink, capsuleTicks, CX, HEADING, INK, LEFT, MUTED, RIGHT, SELECTED, W,
} from '../kit/theme';
import type { MenuAction, PointerInput, SceneHost } from '../scene';
import { drawHelpArt } from './helpArt';
import type { HelpPage, Tone } from './helpPages';
import { capsuleRows, helpPages } from './helpPages';

/** Remembered for the page's life. */
let lastPage = 0;

const TITLE_Y = 30;
const ART: Rect = { x: 8, y: 42, w: 208, h: 82 };
const TEXT_TOP = 132;
const LINE_H = 9;
const POD_TOP = 46;
const POD_H = 13;
const DOTS_Y = 258;
const DOT_W = 12;
const TONES: Readonly<Record<Tone, number>> = { ink: INK, head: HEADING, accent: ACCENT, muted: MUTED };
/** How often the controls page looks again at what is plugged in. */
const LOOK_MS = 500;

export class HelpScene extends UiScene {
  protected readonly heading = 'HOW TO PLAY';
  private page = lastPage;
  private since = 0;
  private looked = 0;
  private pages: HelpPage[];

  constructor(host: SceneHost) {
    super(host);
    this.pages = this.build();
    this.page = clampIndex(this.page, this.pages.length);
  }

  /** The pages, the first for the keys as bound now and the pad or touch controls in use. */
  private build(): HelpPage[] {
    const { keys } = this.services.options.get();
    const input = this.services.input;
    const pair = (a: 'left' | 'right' | 'fire' | 'bomb' | 'pause'): readonly [string, string] => {
      const [k0, k1] = keys[a];
      return [k0 ? input.keyName(k0) : '', k1 ? input.keyName(k1) : ''];
    };
    return helpPages({
      keys: { left: pair('left'), right: pair('right'), fire: pair('fire'), bomb: pair('bomb'), pause: pair('pause') },
      pad: input.gamepad(),
      touch: this.touching(),
    });
  }

  override enter(): void {
    this.pages = this.build();
    super.enter();
    this.since = 0;
  }

  protected override describe(): string {
    const p = this.pages[this.page];
    if (!p) return 'How to play';
    const words = p.id === 'capsules' ? capsuleRows().map((r) => `${r.name}: ${r.text}.`) : [];
    const text = [...words, ...p.lines.map((l) => l.text.trim())].join(' ');
    return `How to play, page ${this.page + 1} of ${this.pages.length}: ${p.title}. ${text}`;
  }

  protected override hint(): string {
    return `◄► PAGE   ${this.words().back} BACK`;
  }

  protected override touchHint(): string {
    return 'TAP A SIDE TO TURN PAGES';
  }

  protected override tick(dt: number): void {
    this.since += dt;
    this.looked += dt;
    if (this.looked < LOOK_MS) return;
    this.looked = 0;
    this.pages = this.build();
  }

  protected paint(screen: Screen): void {
    const p = this.pages[this.page];
    if (!p) return;
    screen.text(p.title, CX, TITLE_Y, SELECTED, 'center');
    const arrow = blink(this.ms, 900) ? SELECTED : MUTED;
    if (this.page > 0) screen.text('◄', LEFT, TITLE_Y, arrow);
    if (this.page < this.pages.length - 1) screen.text('►', RIGHT - 8, TITLE_Y, arrow);
    let top = TEXT_TOP;
    if (p.id === 'capsules') top = this.paintCapsules(screen);
    else {
      drawPanel(screen, ART);
      drawHelpArt(screen, p.id, ART, this.since);
    }
    p.lines.forEach((line, i) => screen.text(line.text, LEFT, top + i * LINE_H, TONES[line.tone]));
    const x0 = CX - (this.pages.length * DOT_W) / 2;
    this.pages.forEach((_, i) => {
      const on = i === this.page;
      screen.text(on ? '◆' : '·', x0 + i * DOT_W + 2, DOTS_Y, on ? SELECTED : MUTED);
    });
  }

  /** Each capsule as the game draws it, its name in its colour, and what it does; returns where the text starts. */
  private paintCapsules(screen: Screen): number {
    const t = capsuleTicks(this.since);
    const rows = capsuleRows();
    rows.forEach((r, i) => {
      const y = POD_TOP + i * POD_H;
      drawCapsule(screen, r.kind, LEFT + 8, y + 4, t);
      screen.text(r.name, LEFT + 20, y, CAPSULE_LOOK[r.kind].colour);
      screen.text(r.text, LEFT + 20 + (r.name.length + 1) * 8, y, INK);
    });
    return POD_TOP + rows.length * POD_H + 8;
  }

  private turn(delta: number): void {
    const next = clampIndex(this.page + delta, this.pages.length);
    if (next === this.page) return this.sound('error');
    this.page = next;
    lastPage = next;
    this.since = 0;
    this.sound('move');
    this.say(this.describe());
  }

  protected onAction(a: MenuAction): void {
    if (a === 'left' || a === 'up') this.turn(-1);
    else if (a === 'right' || a === 'down') this.turn(1);
    else if (a === 'confirm') {
      if (this.page < this.pages.length - 1) this.turn(1);
      else this.back();
    }
  }

  protected override onPointer(p: PointerInput): void {
    if (p.kind === 'wheel') return this.turn(Math.sign(p.delta));
    if (p.kind !== 'down' || p.button !== 0) return;
    if (p.y >= DOTS_Y - 6 && p.y < DOTS_Y + 14) {
      const i = Math.floor((p.x - (CX - (this.pages.length * DOT_W) / 2)) / DOT_W);
      if (i >= 0 && i < this.pages.length && i !== this.page) this.turn(i - this.page);
      return;
    }
    // A tap on the left third turns back, anywhere else turns on.
    this.turn(p.x < W / 3 ? -1 : 1);
  }
}

/** Tests: forget the remembered page. */
export function resetHelpMemory(): void {
  lastPage = 0;
}
